import { useMemo } from 'react';

import { QSelectItem, useFeatureFlag } from '@qualio/ui-components';
import { useTranslation } from 'react-i18next';

import { TemplateParent } from '../../../api/templateApi';

type TemplateOption = {
  label: string;
  properties: { id: number };
  parent?: TemplateParent | null;
};

export type InheritedTemplateOption<TOption extends TemplateOption> = TOption &
  Pick<QSelectItem, 'rightIcon'>;

export const useInheritedTemplateOptions = <TOption extends TemplateOption>(
  options: TOption[],
): InheritedTemplateOption<TOption>[] => {
  const { t } = useTranslation();
  const featureFlags = useFeatureFlag();
  const enterpriseAccounts = Boolean(featureFlags['enterprise-accounts']);

  return useMemo(
    () =>
      options.map((option) => {
        if (!enterpriseAccounts || !option.parent) {
          return option;
        }
        return {
          ...option,
          rightIcon: {
            iconName: 'Related' as const,
            color: 'blue.400',
            tooltip: t('tooltips.inheritedFromParent', {
              parentName: option.parent.name,
            }),
            'data-cy': `inherited-template-icon-${option.properties.id}`,
          },
        };
      }),
    [options, enterpriseAccounts, t],
  );
};
